import { PieceFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import Link from 'next/link';
import React from 'react';
import { buildTrackingId } from '@/utils/tracking-helper';
import { resolveLinkUrlFromChunk } from '../../utils';
import { slugify } from '@/utils/url-helper';

export default function HeaderImageTextComponent({
  components,
  setIsOpen,
  trackingPrefix,
}: Readonly<{
  components: PieceFragment['components'];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trackingPrefix?: string;
}>) {
  const { localizedLink } = useLocaleLink();
  const links = extractComponentFromChunk(components, 'links', 'ContentChunkContent')?.chunks;

  return (
    <div className="space-y-6">
      {links?.map((link, idx) => {
        const title = extractComponentFromChunk(link, 'title', 'SingleLineContent')?.text;
        const description = extractComponentFromChunk(link, 'description', 'RichTextContent');
        const image = extractComponentFromChunk(link, 'image', 'ImageContent');
        const href = resolveLinkUrlFromChunk(link, localizedLink);

        if (!href) {
          return null;
        }

        const navLinkTrackingId = buildTrackingId(trackingPrefix, title);

        return (
          <Link
            key={`${slugify(href)}-${idx}`}
            href={href}
            onClick={() => setIsOpen(false)}
            className="-m-3 flex gap-4 rounded-2xl p-3 duration-200 ease-in hover:bg-neutral-5"
            data-tile-element-id={navLinkTrackingId}
          >
            <OptimizedImage
              imageComponent={image}
              width={100}
              height={100}
              className="h-25 w-25 rounded-md object-cover"
              sizes="120px"
            />
            <div className="">
              <h3 className="text-lg font-bold">{title}</h3>
              <RichTextRenderer blocks={description} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}
